import { useSalesPortalCreateOrderGuideFromMenuMutation } from '@/experiences/sales-portal/blocs/documents/sales-portal-create-order-guide-from-menu.generated';

interface CreateOrderGuideFromMenuMutatorParams {
  onError?: () => void;
  restaurantUUID: string;
  supplierUUID: string;
}

export interface CreatedOrderGuide {
  orderGuideCollectionUUID: string;
  orderGuidePartitionUUID: string;
}

interface CreateOrderGuideFromMenuMutatorResult {
  createOrderGuideFromMenu: () => Promise<CreatedOrderGuide | null>;
  creating: boolean;
}

/**
 * Generates a new order guide for a restaurant based on their online menu.
 *
 * @param params.onError - Invoked when generation fails; lets the consumer
 * decide how to handle the error
 * @param params.restaurantUUID - The UUID of the restaurant to generate for
 * @param params.supplierUUID - The UUID of the supplier
 * @returns {@link CreateOrderGuideFromMenuMutatorResult} — the create function
 * (resolving to the created guide's UUIDs or `null` on failure) and its
 * in-flight state
 *
 * @example
 * ```ts
 * const { showErrorToast } = useExperienceToasts();
 * const { createOrderGuideFromMenu, creating } =
 *   useCreateOrderGuideFromMenuMutator({
 *     onError: showErrorToast,
 *     restaurantUUID,
 *     supplierUUID,
 *   });
 *
 * const handleGenerate = async () => {
 *   const created = await createOrderGuideFromMenu();
 *   // `null` means generation failed and `onError` already ran.
 *   if (!created) {
 *     return;
 *   }
 *   navigateToOrderGuide({
 *     orderGuideUUID: created.orderGuideCollectionUUID,
 *     restaurantUUID,
 *     supplierUUID,
 *   });
 * };
 * ```
 */
export const useCreateOrderGuideFromMenuMutator = ({
  onError,
  restaurantUUID,
  supplierUUID,
}: CreateOrderGuideFromMenuMutatorParams): CreateOrderGuideFromMenuMutatorResult => {
  const [createOrderGuide, { loading: creating }] =
    useSalesPortalCreateOrderGuideFromMenuMutation();

  const createOrderGuideFromMenu =
    async (): Promise<CreatedOrderGuide | null> => {
      try {
        const { data } = await createOrderGuide({
          variables: {
            restaurantUUID,
            supplierUUID,
          },
        });
        const response = data?.createOrderGuideFromMenu;
        return response
          ? {
              orderGuideCollectionUUID: response.order_guide_collection_uuid,
              orderGuidePartitionUUID: response.order_guide_partition_uuid,
            }
          : null;
      } catch {
        onError?.();
        return null;
      }
    };

  return { createOrderGuideFromMenu, creating };
};
